import { gql } from '@apollo/client';

export const GET_WATER_USE_SYSTEM_OPERATIONAL_DAYS = gql`
    query GetWaterUseSystemOperationalDays($pwsId: String!, $years: [Int!]!) {
        getWaterUseSystemOperationalDays(pwsId: $pwsId, years: $years) {
            year
            operationalDays
        }
    }
`;

// Lightweight query for the search/filter page — only fetches years and sources.
export const GET_WATER_USE_SYSTEM_USAGE_YEARS_SOURCES = gql`
    query getWaterUseSystemUsageYearsSources($pwsId: String!) {
        getWaterUseSystemUsageYearsSources(pwsId: $pwsId) {
            years
            sources {
                id
                name
            }
        }
    }
`;

// Fetches core water use system information.
// Fetches core water use system information.
export const GET_WATER_USE_WATER_SYSTEM = gql`
    query getWaterUseSystem($pwsId: String!) {
        getWaterUseSystem(pwsId: $pwsId) {
            systemName
            assignedUser {
                umdId
                firstName
                lastName
            }
            cca
            ccaDate
            engReport
            fileNumber
            d2Link
            standardSet
            peakDayDemand
            averageAnnualDemand
            equalizationStorage
        }
    }
`;

export const GET_WATER_USE_WATER_SYSTEM_ANNUAL_USE = gql`
    query getWaterUseSystemAnnualUse($pwsId: String!, $years: [Int!]!, $sources: [String!]) {
        getWaterUseSystemAnnualUse(pwsId: $pwsId, years: $years, sources: $sources) {
            WaterUseSources {
                sourceId
                sourceName
                sourceStatus
                sourceType
                diversionType
                AnnualRecords {
                    recordYear
                    jan
                    feb
                    mar
                    apr
                    may
                    jun
                    jul
                    aug
                    sep
                    oct
                    nov
                    dec
                    total
                }
            }
            SystemAnnualUse {
                historyYear
                domesticUseACFT
                commercialUseACFT
                industrialUseACFT
                institutionalUseACFT
                totalUseACFT
                domesticUseGallons
                commercialUseGallons
                industrialUseGallons
                institutionalUseGallons
                totalUseGallons
                totalFromSourcesACFT
                totalWholesaleSourcesACFT
                totalReturnSourcesACFT
                nonRevenuePercent
                nonRevenueACFT
                nonRevenueGallons
            }
        }
    }
`;

// Fetches peak day and peak month demand in a single request.
export const GET_WATER_USE_WATER_SYSTEM_PEAK_DATA = gql`
    query getWaterUseSystemPeakData($pwsId: String!, $years: [Int!]!, $sources: [String!]) {
        getWaterUseSystemPeakData(pwsId: $pwsId, years: $years, sources: $sources) {
            peakDemand {
                historyYear
                peakDate
                peakDemandACFT
                peakDemandGallons
                peakUseInclude
                peakMeasurementType
                peakWholesaleVolumeACFT
                peakWholesaleVolumeGallons
            }
            peakMonthDemand {
                historyYear
                peakMonthDemandACFT
                peakMonthDemandGallons
                daysInMonth
                gallonsPerDay
            }
        }
    }
`;

export const GET_WATER_USE_ENGINEERS = gql`
    query getWaterUseEngineers {
        getWaterUseEngineers {
            umdId
            firstName
            lastName
        }
    }
`;

export const UPDATE_WATER_USE_SYSTEM = gql`
    mutation updateWaterUseSystem(
        $UpdateWaterUseSystemInput: UpdateWaterUseSystemInput!
    ) {
        updateWaterUseSystem(
            UpdateWaterUseSystemInput: $UpdateWaterUseSystemInput
        ) {
            assignedUser {
                umdId
                firstName
                lastName
            }
            cca
            ccaDate
            engReport
            fileNumber
            d2Link
            standardSet
            peakDayDemand
            averageAnnualDemand
            equalizationStorage
        }
    }
`;

export const SET_WATER_USE_OPERATIONAL_DAYS = gql`
    mutation setWaterUseOperationalDays($pwsId: String!, $year: Int!, $operationalDays: Int!) {
        setWaterUseOperationalDays(pwsId: $pwsId, year: $year, operationalDays: $operationalDays) {
            year
            operationalDays
        }
    }
`;

export const GET_WATER_USE_SYSTEM_SERVICE_CONNECTIONS = gql`
    query getWaterUseSystemServiceConnectionsPerYear($pwsId: String!, $years: [Int!]!) {
        getWaterUseSystemServiceConnectionsPerYear(pwsId: $pwsId, years: $years) {
            year
            residential
            commercial
            industrial
            institutional
            total
            ercValue
            ercAccepted
            comments
        }
    }
`;

export const SET_WATER_USE_SERVICE_CONNECTION = gql`
    mutation UpdateServiceConnection($updateServiceConnectionInput: UpdateServiceConnectionInput!) {
        updateServiceConnection(UpdateServiceConnectionInput: $updateServiceConnectionInput) {
            year
            residential
            commercial
            industrial
            institutional
            total
            ercValue
            ercAccepted
            comments
        }
    }
`;

export const GET_PEAK_DAY_DEMAND_PER_ERC = gql`
    query GetPeakDayDemandPerErc($pwsId: String!, $years: [Int!]!) {
        getPeakDayDemandPerErc(pwsId: $pwsId, years: $years) {
            pwsId
            year
            peakDate
            peakDayGallonsPerDay
            peakDayWholesaleGallonsPerDay
            peakDayErcGallons
            adjustedPeakDayErc
            changeJustification
        }
    }
`;

export const GET_AVERAGE_ANNUAL_DEMAND_PER_ERC = gql`
    query GetAverageAnnualDemandPerErc($pwsId: String!, $years: [Int!]!, $sources: [String!]) {
        getAverageAnnualDemandPerErc(pwsId: $pwsId, years: $years, sources: $sources) {
            year
            totalFromSourcesGallons
            totalWholesaleSourcesGallons
            totalReturnSourcesGallons
            sourceLessWholesaleGallons
            averageAnnualDemandErc
            adjustedAverageAnnualDemandErc
            changeJustification
        }
    }
`;

export const SET_PEAK_DAY_DEMAND_PER_ERC = gql`
    mutation UpdatePeakDayDemandPerErc($updatePeakDayDemandInput: UpdatePeakDayDemandInput!) {
        updatePeakDayDemandPerErc(UpdatePeakDayDemandInput: $updatePeakDayDemandInput) {
            pwsId
            year
            peakDate
            peakDayGallonsPerDay
            peakDayWholesaleGallonsPerDay
            peakDayErcGallons
            adjustedPeakDayErc
            changeJustification
        }
    }
`;

export const GET_EQUALIZATION_STORAGE_PER_ERC = gql`
    query GetEqualizationStoragePerErc($pwsId: String!, $years: [Int!]!, $sources: [String!]) {
        getEqualizationStoragePerErc(pwsId: $pwsId, years: $years, sources: $sources) {
            pwsId
            year
            averageAnnualDemandErc
            operationalDays
            equalizationStoragePerErcGallons
            adjustedEqualizationStorageErc
            changeJustification
        }
    }
`;

export const SET_EQUALIZATION_STORAGE_PER_ERC = gql`
    mutation UpdateEqualizationStoragePerErc($updateEqualizationStorageInput: UpdateEqualizationStorageInput!) {
        updateEqualizationStoragePerErc(UpdateEqualizationStorageInput: $updateEqualizationStorageInput) {
            pwsId
            year
            averageAnnualDemandErc
            operationalDays
            equalizationStoragePerErcGallons
            adjustedEqualizationStorageErc
            changeJustification
        }
    }
`;

export const GET_PEAK_MONTH_AVG_DEMAND = gql`
    query GetPeakMonthAvgDemand($pwsId: String!, $years: [Int!]!) {
        getPeakMonthAvgDemand(pwsId: $pwsId, years: $years) {
            year
            peakMonthAvgGallons
            peakMonthAvgPerErcGallons
            ratioPeakDayErcPerPeakMonthAvgErc
        }
    }
`;

export const SET_AVERAGE_ANNUAL_DEMAND_PER_ERC = gql`
    mutation UpdateAverageAnnualDemandPerErc($updateAverageAnnualDemandInput: UpdateAverageAnnualDemandInput!) {
        updateAverageAnnualDemandPerErc(UpdateAverageAnnualDemandInput: $updateAverageAnnualDemandInput) {
            pwsId
            year
            totalFromSourcesGallons
            totalWholesaleSourcesGallons
            totalReturnSourcesGallons
            sourceLessWholesaleGallons
            averageAnnualDemandErc
            adjustedAverageAnnualDemandErc
            changeJustification
        }
    }
`;

// Selected Demand per ERC for Minimum Sizing Requirements - each row is one selectedDemandPerErcType (peakDay | averageAnnual | equalizationStorage);
// query and each mutation below return the full recomputed 3-row list.
export const GET_SELECTED_DEMAND_PER_ERC = gql`
    query GetSelectedDemandPerErc($pwsId: String!, $years: [Int!]!, $sources: [String!]) {
        getSelectedDemandPerErc(pwsId: $pwsId, years: $years, sources: $sources) {
            pwsId
            selectedDemandPerErcType
            calculatedErc
            adjustedErc
            justification
        }
    }
`;

export const GET_VARIABILITY_FACTORS = gql`
    query GetVariabilityFactors($pwsId: String!, $years: [Int!]!) {
        getVariabilityFactors(pwsId: $pwsId, years: $years) {
            type
            pwsId
            variability
            adjustedValue
            justification
        }
    }
`;

// Single mutation handles all 3 selectedDemandPerErcTypes (peakDay | averageAnnual | equalizationStorage) via the selectedDemandPerErcType field on the input.
export const SET_SELECTED_DEMAND_PER_ERC = gql`
    mutation UpdateSelectedDemandPerErc($updateSelectedDemandInput: UpdateSelectedDemandInput!) {
        updateSelectedDemandPerErc(UpdateSelectedDemandInput: $updateSelectedDemandInput) {
            pwsId
            selectedDemandPerErcType
            calculatedErc
            adjustedErc
            justification
        }
    }
`;

export const SET_VARIABILITY_FACTOR = gql`
    mutation UpdateVariabilityFactor($updateVariabilityFactorInput: UpdateVariabilityFactorInput!) {
        updateVariabilityFactor(UpdateVariabilityFactorInput: $updateVariabilityFactorInput) {
            type
            pwsId
            variability
            adjustedValue
            justification
        }
    }
`;
